import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  FlaskConical, 
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle,
  Lock,
  ExternalLink,
  Play,
  Square,
  RefreshCw,
  ArrowLeft,
  Server,
  Activity,
  User
} from 'lucide-react';
import { studentPortal } from '../services/api';
import WorkspaceImportDialog from '../components/WorkspaceImportDialog';
import { labLabel } from '../utils/labNaming';
import { format, formatDistanceToNow, isPast } from 'date-fns';

// Note: The IDE launch flow now uses POST-based authentication for security.
// The token is sent in the request body, not the URL, and a cookie is set.

// The IDE tab is opened blank at the click (pop-up blockers allow nothing
// later) and may wait a minute for a paused environment: say so in it.
function showInTab(tab, text) {
  try {
    tab.document.title = 'Virtual Labs';
    tab.document.body.style.cssText = 'font-family: system-ui, sans-serif; display: grid; place-items: center; min-height: 90vh; margin: 0; color: #4b5563;';
    tab.document.body.textContent = text;
  } catch { /* closed, or already navigated */ }
}

function StudentLabView() {
  const { labId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [launchingIDE, setLaunchingIDE] = useState(false);
  const [openingPreview, setOpeningPreview] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showImports, setShowImports] = useState(searchParams.get('import') === '1');

  const { data: labData, isLoading, error, refetch } = useQuery({
    queryKey: ['studentLab', labId],
    queryFn: () => studentPortal.getLab(labId),
    refetchInterval: 10000, // Refresh every 10s for container status
    placeholderData: { lab: null },
  });

  const lab = labData?.lab;
  const container = lab?.container;
  const isContainerRunning = container?.status === 'running';
  // Paused after a long stretch without activity. Launching starts it again;
  // only a stop by the instructor needs them to act.
  const isPaused = container?.paused === true;
  const isStarting = container?.status === 'creating';
  const canLaunch = isContainerRunning || isPaused || isStarting;
  const [authError, setAuthError] = useState(null);
  const [startingMessage, setStartingMessage] = useState(null);

  // Extract session token from container URL (it's in the query param)
  const getSessionToken = () => {
    if (container?.sessionToken) return container.sessionToken;
    if (!container?.url) return null;
    try {
      const url = new URL(container.url);
      return url.searchParams.get('token');
    } catch {
      return null;
    }
  };

  // Handle launching the IDE with secure POST-based authentication. An
  // environment that was paused starts again here: the backend answers 202
  // while it starts, and this polls until the IDE is ready.
  const handleLaunchIDE = async (importId) => {
    let sessionToken = getSessionToken();

    if (!sessionToken) {
      const error = new Error('Refresh the lab page before opening the IDE.');
      setAuthError(error.message);
      if (importId) throw error;
      return;
    }

    setLaunchingIDE(true);
    setAuthError(null);
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    showInTab(tab, 'Opening your lab environment…');

    try {
      // POST to authenticate endpoint - this sets the cookie
      let result = await studentPortal.authenticateForIDE(labId, sessionToken, importId);
      const deadline = Date.now() + 5 * 60 * 1000;
      while (result.starting) {
        setStartingMessage(result.message);
        showInTab(tab, result.message);
        if (Date.now() > deadline) {
          throw new Error('Your environment is taking longer than usual to start. Try again in a minute.');
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
        // A restart can issue a new session token: use the current one.
        const fresh = await refetch();
        sessionToken = fresh.data?.lab?.container?.sessionToken || sessionToken;
        result = await studentPortal.authenticateForIDE(labId, sessionToken, importId);
      }

      // Open the clean URL (cookie is now set)
      if (result.url) {
        if (tab) tab.location.replace(result.url);
        else throw new Error('Allow pop-ups for this site, then open the IDE again.');
      }
    } catch (error) {
      console.error('[IDE] Authentication failed:', error);
      tab?.close();
      setAuthError(error.response?.data?.message || error.message || 'Failed to authenticate. Please try again.');
      if (importId) throw error;
    } finally {
      setStartingMessage(null);
      setTimeout(() => setLaunchingIDE(false), 1000);
      queryClient.invalidateQueries({ queryKey: ['studentLab', labId] });
    }
  };

  // Open the workspace preview on its own per-pod origin. The backend returns a
  // one-hop URL that plants a cookie scoped to that origin and strips the token,
  // so nothing sensitive stays in the address bar. Opened in a new tab.
  const handleOpenPreview = async () => {
    setOpeningPreview(true);
    setAuthError(null);
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const result = await studentPortal.openPreview(labId);
      if (result.url) {
        if (tab) tab.location.replace(result.url);
        else throw new Error('Allow pop-ups for this site, then open the preview again.');
      }
    } catch (error) {
      tab?.close();
      setAuthError(error.response?.data?.error || error.message || 'Failed to open the preview.');
    } finally {
      setTimeout(() => setOpeningPreview(false), 1000);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-fade-in" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }} role="status" aria-label="Loading lab">
        <div className="spinner" style={{ width: 48, height: 48 }} aria-hidden="true" />
        <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Loading lab...</span>
      </div>
    );
  }

  if (error || !lab) {
    return (
      <div className="animate-fade-in" style={{ padding: 40, textAlign: 'center' }} role="alert">
        <AlertCircle size={48} style={{ color: 'var(--accent-danger)', marginBottom: 16 }} aria-hidden="true" />
        <h2>Unable to load lab</h2>
        <p className="text-muted" style={{ marginBottom: 20 }}>
          {error?.response?.data?.message || 'This lab may not exist or you may not have access to it.'}
        </p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
      </div>
    );
  }

  const isPastDue = lab.dueDate && isPast(new Date(lab.dueDate)) && lab.isAccessible;

  return (
    <div className="animate-fade-in">
      {/* Back Button */}
      <button 
        className="btn btn-secondary btn-sm"
        onClick={() => navigate('/')}
        style={{ marginBottom: 24 }}
      >
        <ArrowLeft size={16} />
        Back to Dashboard
      </button>

      {/* Lab Header */}
      <div className="card" style={{ marginBottom: 24, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'var(--accent-purple)20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <FlaskConical size={24} style={{ color: 'var(--accent-purple)' }} />
              </div>
              <div>
                <h1 style={{ fontSize: 24, marginBottom: 4 }}>{lab.name}</h1>
                <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                  {lab.courseCode} {lab.section && `• Section ${lab.section}`} • {labLabel(lab)}
                </p>
              </div>
            </div>
            
            {lab.description && (
              <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginTop: 12, maxWidth: 600 }}>
                {lab.description}
              </p>
            )}
          </div>

          <div>
            {lab.isAccessible ? (
              <span className="badge badge-success" style={{ fontSize: 14, padding: '6px 12px' }}>
                <CheckCircle size={14} />
                Lab Open
              </span>
            ) : (
              <span className="badge badge-secondary" style={{ fontSize: 14, padding: '6px 12px' }}>
                <Lock size={14} />
                Lab Closed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Dates Info */}
      <div className="card" style={{ marginBottom: 24, padding: 20 }}>
        <h3 style={{ fontSize: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Calendar size={18} />
          Important Dates
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16
        }}>
          {lab.openDate && (
            <DateCard 
              label="Opens"
              date={lab.openDate}
              icon={Play}
              color="var(--accent-success)"
            />
          )}
          {lab.dueDate && (
            <DateCard 
              label="Due"
              date={lab.dueDate}
              icon={AlertCircle}
              color={isPastDue ? 'var(--accent-danger)' : 'var(--accent-warning)'}
              highlight={isPastDue}
            />
          )}
          {lab.closeDate && (
            <DateCard 
              label="Closes"
              date={lab.closeDate}
              icon={Lock}
              color="var(--accent-secondary)"
            />
          )}
        </div>
      </div>

      {/* IDE Environment */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ fontSize: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Server size={18} />
          Your Lab Environment
        </h3>

        {/* D2L Security Lock - Lab requires D2L access */}
        {lab.isD2LLocked ? (
          <div style={{ 
            padding: 32, 
            background: 'var(--accent-warning)08',
            borderRadius: 8,
            textAlign: 'center',
            border: '1px solid var(--accent-warning)30'
          }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'var(--accent-warning)15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Lock size={32} style={{ color: 'var(--accent-warning)' }} />
            </div>
            <h4 style={{ marginBottom: 8, color: 'var(--accent-warning)' }}>D2L Access Required</h4>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16, maxWidth: 400, margin: '0 auto 16px' }}>
              {lab.d2lLockMessage || 'This lab requires access through D2L for security reasons. Please launch from your D2L course page.'}
            </p>
            <div style={{ 
              padding: 12, 
              background: 'var(--bg-tertiary)', 
              borderRadius: 6, 
              fontSize: 13,
              color: 'var(--text-muted)',
              maxWidth: 400,
              margin: '0 auto'
            }}>
              <strong>How to access:</strong>
              <ol style={{ textAlign: 'left', paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
                <li>Go to D2L (Brightspace)</li>
                <li>Navigate to your course</li>
                <li>Find the lab link provided by your instructor</li>
                <li>Click the link to access your lab environment</li>
              </ol>
            </div>
          </div>
        ) : !lab.isAccessible ? (
          <div style={{ 
            padding: 32, 
            background: 'var(--bg-tertiary)',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <Lock size={48} style={{ color: 'var(--text-muted)', marginBottom: 16, opacity: 0.5 }} />
            <h4 style={{ marginBottom: 8 }}>Lab Not Accessible</h4>
            <p className="text-muted">
              This lab is currently not available. Please check the dates above or contact your instructor.
            </p>
          </div>
        ) : !container ? (
          <div style={{ 
            padding: 32, 
            background: 'var(--bg-tertiary)',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <Server size={48} style={{ color: 'var(--text-muted)', marginBottom: 16, opacity: 0.5 }} />
            <h4 style={{ marginBottom: 8 }}>Environment Not Ready</h4>
            <p className="text-muted" style={{ marginBottom: 16 }}>
              Your lab environment hasn't been set up yet. Please contact your instructor.
            </p>
            <button className="btn btn-secondary" onClick={() => refetch()}>
              <RefreshCw size={16} />
              Check Again
            </button>
          </div>
        ) : (
          <div>
            {/* Container Status */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 16,
                background: 'var(--bg-tertiary)',
                borderRadius: 8,
                marginBottom: 20
              }}
              role="status"
              aria-label={`Environment is ${isContainerRunning ? 'running' : isStarting ? 'starting' : isPaused ? 'paused' : 'stopped'}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: isContainerRunning ? 'var(--accent-success)' : 'var(--accent-warning)',
                    boxShadow: isContainerRunning ? '0 0 8px var(--accent-success)' : 'none'
                  }}
                  aria-hidden="true"
                />
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {isContainerRunning ? 'Environment Running'
                      : isStarting ? 'Environment Starting'
                        : isPaused ? 'Environment Paused' : 'Environment Stopped'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {container.lastAccessed 
                      ? `Last accessed ${formatDistanceToNow(new Date(container.lastAccessed), { addSuffix: true })}`
                      : 'Never accessed'
                    }
                  </div>
                </div>
              </div>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => refetch()}
                title="Refresh status"
                aria-label="Refresh environment status"
              >
                <RefreshCw size={14} aria-hidden="true" />
              </button>
            </div>

            {/* Launch Button */}
            {canLaunch ? (
              <div style={{ textAlign: 'center' }}>
                {isPaused && !launchingIDE && (
                  <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-muted)' }}>
                    Your environment was paused after a long time without activity. Your files are saved.
                    Launching the IDE starts it again, which takes about a minute.
                  </p>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => handleLaunchIDE()}
                  disabled={launchingIDE}
                  style={{ 
                    padding: '16px 32px',
                    fontSize: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  {launchingIDE ? (
                    <>
                      <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                      {startingMessage ? 'Starting environment…' : 'Opening IDE...'}
                    </>
                  ) : (
                    <>
                      <ExternalLink size={20} />
                      Launch IDE
                    </>
                  )}
                </button>
                <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }} role="status" aria-live="polite">
                  {startingMessage || 'The IDE will open in a new browser tab'}
                </p>
                {lab.clipboardPolicy === 'block' && (
                  <p style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)' }}>
                    Copy and paste are turned off in this lab: type your code and commands.
                  </p>
                )}
                {lab.clipboardPolicy === 'within_ide' && (
                  <p style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)' }}>
                    In this lab you can copy and paste only within the IDE.
                  </p>
                )}
                {(lab.allowWorkspaceUpload || container.hasWorkspaceImports) && <button className="btn btn-secondary"
                  style={{ marginTop: 12 }} onClick={() => setShowImports(true)}>
                  {lab.allowWorkspaceUpload ? 'Import existing code' : 'Imported projects'}
                </button>}
                {lab.servesPreview && isContainerRunning && <button className="btn btn-secondary"
                  style={{ marginTop: 12, marginLeft: lab.allowWorkspaceUpload || container.hasWorkspaceImports ? 8 : 0,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={handleOpenPreview} disabled={openingPreview}>
                  <ExternalLink size={16} />
                  {openingPreview ? 'Opening preview...' : 'Open preview'}
                </button>}
                {authError && (
                  <p role="alert" style={{ marginTop: 8, fontSize: 13, color: 'var(--accent-danger)' }}>
                    {authError}
                  </p>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div
                  role="alert"
                  style={{
                    padding: 20,
                    background: 'var(--accent-warning)10',
                    borderRadius: 8,
                    marginBottom: 16
                  }}
                >
                  <AlertCircle size={24} style={{ color: 'var(--accent-warning)', marginBottom: 8 }} aria-hidden="true" />
                  <p style={{ fontWeight: 500 }}>Environment is stopped</p>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Please wait while your instructor starts the environment, or contact them if this persists.
                  </p>
                </div>
                <button className="btn btn-secondary" onClick={() => refetch()}>
                  <RefreshCw size={16} />
                  Check Status
                </button>
              </div>
            )}

            {/* Container Info */}
            {container && (
              <div style={{ 
                marginTop: 24,
                paddingTop: 20,
                borderTop: '1px solid var(--border-primary)',
                fontSize: 13,
                color: 'var(--text-muted)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                  <div>
                    <span>Access Count: </span>
                    <strong>{container.accessCount || 0}</strong>
                  </div>
                  {container.port && (
                    <div>
                      <span>Port: </span>
                      <code>{container.port}</code>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showImports && <WorkspaceImportDialog labId={labId}
        allowUpload={lab.allowWorkspaceUpload && lab.isAccessible}
        ready={isContainerRunning && !container?.workspaceSeedPending && lab.isAccessible}
        onOpenProject={handleLaunchIDE}
        onClose={() => {
          setShowImports(false);
          const next = new URLSearchParams(searchParams);
          next.delete('import'); setSearchParams(next, { replace: true });
        }} />}

      {/* Instructor Info */}
      {lab.instructor && (
        <div className="card" style={{ marginTop: 24, padding: 20 }}>
          <h3 style={{ fontSize: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <User size={18} />
            Instructor
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'var(--accent-primary)20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <User size={20} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 500 }}>{lab.instructor.name}</div>
              <a 
                href={`mailto:${lab.instructor.email}`}
                style={{ fontSize: 13, color: 'var(--accent-primary)' }}
              >
                {lab.instructor.email}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DateCard({ label, date, icon: Icon, color, highlight }) {
  const dateObj = new Date(date);
  const isPastDate = isPast(dateObj);
  
  return (
    <div style={{
      padding: 16,
      background: highlight ? `${color}10` : 'var(--bg-tertiary)',
      borderRadius: 8,
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 8,
        marginBottom: 8,
        color: highlight ? color : 'var(--text-secondary)',
        fontWeight: 500,
        fontSize: 13
      }}>
        <Icon size={14} />
        {label}
      </div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {format(dateObj, 'MMM d, yyyy')}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {format(dateObj, 'h:mm a')}
      </div>
      <div style={{ 
        fontSize: 12, 
        marginTop: 8,
        color: isPastDate ? 'var(--text-muted)' : color
      }}>
        {formatDistanceToNow(dateObj, { addSuffix: true })}
      </div>
    </div>
  );
}

export default StudentLabView;

